// ---------------------------------------------------------------------------
// SCE-UA (Shuffled Complex Evolution — University of Arizona; Duan,
// Sorooshian & Gupta, Water Resources Research 28(4), 1992) — ported from
// CalEvBasic's SCEUA.vb, confirmed line-by-line faithful to the published
// algorithm (population partitioned into complexes, each complex evolved via
// competitive complex evolution / simplex reflection-contraction, complexes
// re-shuffled after each round). Generic here: `evaluate` is any function of
// a parameter vector to MAXIMIZE — this app's own hydrology engine plugs in
// as `evaluate` only when a real calibration runs (Phase 3), not in this
// module, which knows nothing about EvalHid's models or observed series.
//
// Two real quirks in the source, replicated on purpose rather than "fixed"
// — same policy this app already applies to the main calculation engine
// (see CLAUDE.md):
//
// 1. **`SCEUA()` minimizes, `OFCalculation()` maximizes** — `FUNCTN()`
//    (SCEUA.vb) returns `-OFCalculation()`. `evaluate` here follows
//    `OFCalculation`'s convention (maximize, matching objectiveFunction.ts);
//    negation into minimize-direction happens once, internally.
// 2. **The convergence range (`gnrng`) quirk**: SCEUA.vb's `promV`, used to
//    average the per-parameter normalized log-range into `gnrng`, sums
//    `nopt` (parameter count) terms but divides by `npt` (population size:
//    `(2·nopt+1)·ngs`) instead of `nopt` — a real bug in the source, not the
//    textbook SCE-UA formula. Since `npt` is always larger than `nopt` for
//    any real `ngs`, this makes `gnrng` systematically smaller than the
//    textbook geometric-range would be, which makes the `peps`
//    parameter-space convergence criterion trigger noticeably EARLIER than
//    a textbook SCE-UA implementation. Kept as-is: this module's whole
//    purpose is fidelity to CalEvBasic's actual (already-verified) behavior
//    of maximizing/minimizing an objective function, not a from-scratch
//    "correct" SCE-UA.
//
// Deliberately NOT ported: evaluating the project's CURRENT (pre-
// calibration) parameters as a baseline before the search starts
// (AlgoritmoCalculo.vb's `PreProcess`, which seeds `BestOF`/fires
// `OFMejoradaEvent` once for "iteration 0"). This module only tracks bests
// found by its own search; a caller that wants to compare against the
// project's existing parameters can call `evaluate` on them once, before
// calling `runSceUa`, and compare the two values itself (see
// project_evalhid_calibracion.md — Phase 4's report/apply step).
//
// `runSceUa` is async and yields to the event loop every
// `YIELD_EVERY_EVALUATIONS` evaluations (a plain macrotask via `setTimeout`)
// — not needed for the algorithm itself (`evaluate` is a synchronous, pure
// function), only so a caller re-running this app's own engine hundreds of
// times on the browser's main thread (no Web Worker, see
// runCalibration.ts's header) still lets React repaint a live progress
// chart (Phase 4's `onImprovement`) between evaluations, instead of
// freezing the tab for the whole run and only painting once at the end.
// ---------------------------------------------------------------------------

import { ParkMillerRandom } from "./parkMillerRandom.ts";

interface SceUaConfig {
  /** Same length as `lowerBounds`/`upperBounds`. */
  numParams: number;
  /** Must have `upperBounds[i] > lowerBounds[i]` for every parameter actually being calibrated — exclude any fixed parameter from these arrays entirely rather than giving it a zero-width range. */
  lowerBounds: number[];
  upperBounds: number[];
  /** The value to MAXIMIZE for a given parameter vector (e.g. objectiveFunction() from objectiveFunction.ts, wired by the caller to run this app's engine and compare against an observed series). */
  evaluate: (params: number[]) => number;
  /** Maximum number of `evaluate()` calls. VB default (`mMaxIter`): 7000. */
  maxEvaluations?: number;
  /** Number of complexes (sub-populations). VB default (`mNumGroups`): 3. */
  numComplexes?: number;
  /** Shuffling loops without a significant objective-function change before stopping. VB default (`mKstop`): 10. */
  kstop?: number;
  /** Objective-function-change threshold (see `objectiveConverged` below) below which the search is considered converged. VB default (`mPcento`): 0.1. */
  pcento?: number;
  /** Parameter-space convergence threshold — see the `gnrng` quirk above. VB default (`mPeps`): 0.001. */
  peps?: number;
  /** PRNG seed (see parkMillerRandom.ts) — same seed + same `evaluate` always produces the same run. Defaults to a random seed. */
  seed?: number;
  /** Fires once per NEW best found across ANY evaluation (initial population or during the search) — mirrors `OFMejoradaEvent`, the hook for a live progress chart (Phase 4). */
  onImprovement?: (evaluations: number, bestValue: number, bestParams: number[]) => void;
}

export type SceUaStopReason = "maxEvaluations" | "parameterSpaceConverged" | "objectiveConverged";

interface SceUaResult {
  bestParams: number[];
  bestValue: number;
  evaluations: number;
  stopReason: SceUaStopReason;
  /** One entry per improvement — same events as `onImprovement`. */
  history: { evaluations: number; bestValue: number }[];
}

const DEFAULT_MAX_EVALUATIONS = 7000;
const DEFAULT_NUM_COMPLEXES = 3;
const DEFAULT_KSTOP = 10;
const DEFAULT_PCENTO = 0.1;
const DEFAULT_PEPS = 0.001;
const YIELD_EVERY_EVALUATIONS = 15;

function yieldToUI(): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, 0));
}

function randomPointWithinBounds(
  lowerBounds: number[],
  boundWidths: number[],
  rng: ParkMillerRandom
): number[] {
  return lowerBounds.map((lb, j) => lb + rng.next() * boundWidths[j]);
}

/** Sorts `population`/`values` in place, ascending by `values` (stable) — SCEUA.vb's `RankX`/`RankC`, identical logic for both the full population and a single complex. */
function rankAscending(population: number[][], values: number[]): void {
  const order = values.map((_, i) => i).sort((a, b) => values[a] - values[b]);
  const sortedPopulation = order.map((i) => population[i]);
  const sortedValues = order.map((i) => values[i]);
  for (let i = 0; i < population.length; i++) {
    population[i] = sortedPopulation[i];
    values[i] = sortedValues[i];
  }
}

/** SCEUA.vb's `promV`/`MAxValVector`/`MinValVector`, combined — see this module's header for the `npt`-vs-`nopt` divisor quirk. */
function normalizedGeometricRange(population: number[][], boundWidths: number[]): number {
  const numParams = boundWidths.length;
  let sumLogRange = 0;
  for (let j = 0; j < numParams; j++) {
    let max = -Infinity;
    let min = Infinity;
    for (const point of population) {
      if (point[j] > max) max = point[j];
      if (point[j] < min) min = point[j];
    }
    sumLogRange += Math.log((max - min) / boundWidths[j]);
  }
  return Math.exp(sumLogRange / population.length);
}

/**
 * SCEUA.vb's `CCEUA` — evolves one simplex (`simplex`/`simplexValues`,
 * sorted ascending, minimize-direction) by one step: reflect the worst
 * point through the centroid of the rest; if that's out of bounds, replace
 * it with a uniformly random point instead; if the result is still worse
 * than the simplex's worst point, contract toward the centroid instead; if
 * that's STILL worse, accept one final uniformly random point unconditionally
 * (whether it's actually any good or not — faithful to the source, which
 * never rejects that last fallback).
 */
async function competitiveComplexEvolution(
  simplex: number[][],
  simplexValues: number[],
  lowerBounds: number[],
  upperBounds: number[],
  boundWidths: number[],
  rng: ParkMillerRandom,
  evaluateMinimize: (params: number[]) => Promise<number>
): Promise<{ newParams: number[]; newValue: number }> {
  const numParams = lowerBounds.length;
  const nps = simplex.length;
  const alpha = 1.0;
  const beta = 0.5;

  const worst = simplex[nps - 1];
  const worstValue = simplexValues[nps - 1];

  const centroid = new Array<number>(numParams).fill(0);
  for (let i = 0; i < nps - 1; i++) {
    for (let j = 0; j < numParams; j++) centroid[j] += simplex[i][j];
  }
  for (let j = 0; j < numParams; j++) centroid[j] /= nps - 1;

  let candidate = centroid.map((c, j) => c + alpha * (c - worst[j]));
  const outOfBounds = candidate.some((v, j) => v < lowerBounds[j] || v > upperBounds[j]);
  if (outOfBounds) candidate = randomPointWithinBounds(lowerBounds, boundWidths, rng);
  let candidateValue = await evaluateMinimize(candidate);

  if (candidateValue > worstValue) {
    candidate = centroid.map((c, j) => worst[j] + beta * (c - worst[j]));
    candidateValue = await evaluateMinimize(candidate);

    if (candidateValue > worstValue) {
      candidate = randomPointWithinBounds(lowerBounds, boundWidths, rng);
      candidateValue = await evaluateMinimize(candidate);
    }
  }

  return { newParams: candidate, newValue: candidateValue };
}

export async function runSceUa(config: SceUaConfig): Promise<SceUaResult> {
  const {
    numParams,
    lowerBounds,
    upperBounds,
    evaluate,
    maxEvaluations = DEFAULT_MAX_EVALUATIONS,
    numComplexes = DEFAULT_NUM_COMPLEXES,
    kstop = DEFAULT_KSTOP,
    pcento = DEFAULT_PCENTO,
    peps = DEFAULT_PEPS,
    seed = Math.floor(Math.random() * 999999) + 1,
    onImprovement,
  } = config;

  const rng = new ParkMillerRandom(seed);
  const boundWidths = lowerBounds.map((lb, i) => upperBounds[i] - lb);

  const npg = 2 * numParams + 1; // points per complex
  const nps = numParams + 1; // points per simplex
  const populationSize = npg * numComplexes;

  let evaluations = 0;
  let bestValue = -Infinity;
  let bestParams: number[] = [];
  const history: { evaluations: number; bestValue: number }[] = [];

  async function evaluateMinimize(params: number[]): Promise<number> {
    const maximizeValue = evaluate(params);
    evaluations++;
    if (maximizeValue > bestValue) {
      bestValue = maximizeValue;
      bestParams = params.slice();
      history.push({ evaluations, bestValue });
      onImprovement?.(evaluations, bestValue, bestParams);
    }
    if (evaluations % YIELD_EVERY_EVALUATIONS === 0) await yieldToUI();
    return -maximizeValue;
  }

  const population: number[][] = Array.from({ length: populationSize }, () =>
    randomPointWithinBounds(lowerBounds, boundWidths, rng)
  );
  const popValues: number[] = [];
  for (const point of population) popValues.push(await evaluateMinimize(point));
  rankAscending(population, popValues);

  let gnrng = normalizedGeometricRange(population, boundWidths);

  let nloop = 0;
  let criterChange = 100000;
  const criterHistory: number[] = [];

  outer: while (evaluations < maxEvaluations && gnrng > peps && criterChange > pcento) {
    nloop++;

    for (let igs = 0; igs < numComplexes; igs++) {
      const complexIndices: number[] = [];
      for (let i = 0; i < npg; i++) complexIndices.push(i * numComplexes + igs);

      const complexPop = complexIndices.map((idx) => population[idx]);
      const complexValues = complexIndices.map((idx) => popValues[idx]);

      for (let loopi = 0; loopi < npg; loopi++) {
        // Re-check the budget before every simplex evolution (each of which
        // can call evaluateMinimize up to 3 times), not just once per full
        // shuffle round — the outer `while` guard alone let a run overshoot
        // `maxEvaluations` by up to numComplexes*npg*3 evaluations (a whole
        // round's worth) before stopping. This bounds the overshoot to at
        // most 3 (the previous iteration's evolution, already in flight when
        // this check runs).
        if (evaluations >= maxEvaluations) break outer;

        // Select a simplex of `nps` points from the complex via a triangular
        // probability distribution biased toward better-ranked points —
        // SCEUA.vb's own formula, the best point (index 0) always included.
        const chosen: number[] = [0];
        for (let k3 = 1; k3 < nps; k3++) {
          let candidateIndex = 0;
          for (let attempt = 0; attempt < 1000; attempt++) {
            candidateIndex = Math.floor(
              npg + 0.5 - Math.sqrt((npg + 0.5) ** 2 - npg * (npg + 1) * rng.next())
            );
            if (!chosen.includes(candidateIndex)) break;
          }
          chosen.push(candidateIndex);
        }
        chosen.sort((a, b) => a - b);

        const simplex = chosen.map((idx) => complexPop[idx]);
        const simplexValues = chosen.map((idx) => complexValues[idx]);

        const { newParams, newValue } = await competitiveComplexEvolution(
          simplex,
          simplexValues,
          lowerBounds,
          upperBounds,
          boundWidths,
          rng,
          evaluateMinimize
        );
        simplex[simplex.length - 1] = newParams;
        simplexValues[simplexValues.length - 1] = newValue;

        chosen.forEach((idx, i) => {
          complexPop[idx] = simplex[i];
          complexValues[idx] = simplexValues[i];
        });
        rankAscending(complexPop, complexValues);
      }

      complexIndices.forEach((idx, i) => {
        population[idx] = complexPop[i];
        popValues[idx] = complexValues[i];
      });
    }

    rankAscending(population, popValues);
    gnrng = normalizedGeometricRange(population, boundWidths);

    criterHistory.push(popValues[0]);
    if (nloop >= kstop) {
      criterChange = Math.abs(criterHistory[nloop - 1] - criterHistory[nloop - kstop]) * 100;
      let meanAbsCriterion = 0;
      for (let k = nloop - kstop; k <= nloop - 1; k++)
        meanAbsCriterion += Math.abs(criterHistory[k]);
      meanAbsCriterion /= kstop - 1;
      criterChange /= meanAbsCriterion;
    }
  }

  let stopReason: SceUaStopReason = "maxEvaluations";
  if (evaluations >= maxEvaluations) stopReason = "maxEvaluations";
  else if (gnrng <= peps) stopReason = "parameterSpaceConverged";
  else if (criterChange <= pcento) stopReason = "objectiveConverged";

  return { bestParams, bestValue, evaluations, stopReason, history };
}
